<lib-sales-insights-card
  [cardTitle]="config?.title"
  [subtitle]="subtitle"
  [loading]="loading"
  [error]="error"
  [empty]="empty"
>
  @if (canManage) {
    <button cardActions type="button" class="btn btn-sm si-goals__button" (click)="openForm(null, goalForm)">
      <i class="fa-solid fa-plus"></i>{{ 'SALES_INSIGHTS_GOAL_NEW' | translate }}
    </button>
  }
  @if (goals.length) {
    <div class="si-goals">
      @for (goal of goals; track goal.id; let i = $index) {
        <div
          class="si-goals__goal"
          [class.si-goals__goal--expired]="goal.expired"
          role="button"
          tabindex="0"
          [attr.aria-label]="goal.title"
          (click)="openCustomers(goal, goalCustomersModal)"
          (keydown.enter)="openCustomers(goal, goalCustomersModal)"
        >
          <div class="si-goals__head">
            <span class="si-goals__title" [title]="goal.title">{{ goal.title }}</span>
            @if (goal.expired) {
              <span class="si-goals__pill si-goals__pill--muted">{{ 'SALES_INSIGHTS_GOAL_EXPIRED' | translate }}</span>
            } @else {
              <span class="si-goals__pill" [class.si-goals__pill--done]="goal.total > 0 && goal.remaining === 0">
                {{ percentText(goal) }}
              </span>
            }
          </div>
          <div class="si-goals__value">
            {{ formatAmount(goal.done) }}
            <small>{{ 'SALES_INSIGHTS_OF_TOTAL' | translate: { total: formatAmount(goal.total) } }}</small>
          </div>
          <div class="si-goals__bar" aria-hidden="true">
            <span [style.width.%]="percent(goal)" [style.background]="barColor(goal, i)"></span>
          </div>
          <div class="si-goals__foot">
            <span class="si-goals__meta">
              {{ 'SALES_INSIGHTS_GOAL_REMAINING' | translate: { count: formatAmount(goal.remaining) } }}
              @if (goal.deadline) {
                · {{ 'SALES_INSIGHTS_GOAL_DEADLINE' | translate }} <b>{{ goal.deadline }}</b>
              }
            </span>
            @if (hasSalespersonBreakdown(goal)) {
              <button
                type="button"
                class="si-goals__toggle"
                [attr.aria-expanded]="expandedGoalIds.has(goal.id)"
                (click)="toggleSalespersons(goal, $event)"
                (keydown.enter)="$event.stopPropagation()"
              >
                {{ 'SALES_INSIGHTS_GOAL_PER_SALESPERSON' | translate }}
                <i class="fa-solid" [class.fa-chevron-down]="!expandedGoalIds.has(goal.id)" [class.fa-chevron-up]="expandedGoalIds.has(goal.id)"></i>
              </button>
            }
            @if (canManage) {
              <span class="si-goals__manage">
                <button
                  type="button"
                  class="si-goals__icon"
                  [title]="'SALES_INSIGHTS_GOAL_EDIT' | translate"
                  [attr.aria-label]="'SALES_INSIGHTS_GOAL_EDIT' | translate"
                  (click)="openForm(goal, goalForm, $event)"
                  (keydown.enter)="$event.stopPropagation()"
                >
                  <i class="fa-regular fa-pen-to-square"></i>
                </button>
                <button
                  type="button"
                  class="si-goals__icon si-goals__icon--danger"
                  [title]="'SALES_INSIGHTS_GOAL_DELETE' | translate"
                  [attr.aria-label]="'SALES_INSIGHTS_GOAL_DELETE' | translate"
                  (click)="deleteGoal(goal, $event)"
                  (keydown.enter)="$event.stopPropagation()"
                >
                  <i class="fa-regular fa-trash-can"></i>
                </button>
              </span>
            }
          </div>
          @if (expandedGoalIds.has(goal.id) && goal.salespersons?.length) {
            <ul class="si-goals__salespersons">
              @for (salesperson of goal.salespersons!; track salesperson.code) {
                <li>
                  <span class="si-goals__salesperson-name">
                    {{ salesperson.code ? salesperson.name || salesperson.code : ('SALES_INSIGHTS_GOAL_NO_SALESPERSON' | translate) }}
                  </span>
                  <span class="si-goals__salesperson-count">{{ formatAmount(salesperson.done) }}/{{ formatAmount(salesperson.total) }}</span>
                  <span class="si-goals__bar si-goals__bar--small" aria-hidden="true">
                    <span [style.width.%]="salespersonPercent(salesperson)" [style.background]="barColor(goal, i)"></span>
                  </span>
                </li>
              }
            </ul>
          }
        </div>
      }
    </div>
  } @else {
    <div class="si-goals__empty">{{ 'SALES_INSIGHTS_GOAL_NONE' | translate }}</div>
  }
</lib-sales-insights-card>

<ng-template #goalCustomersModal let-modal>
  <div class="modal-header si-goals__modal-header">
    <div>
      <h4 class="modal-title">{{ openGoal?.title }}</h4>
      @if (openGoal) {
        <div class="si-goals__modal-sub">
          {{ 'SALES_INSIGHTS_GOAL_PROGRESS' | translate: { done: formatAmount(openGoal.done), total: formatAmount(openGoal.total) } }}
          @if (openGoal.deadline) {
            · {{ 'SALES_INSIGHTS_GOAL_DEADLINE' | translate }} {{ openGoal.deadline }}
          }
        </div>
      }
    </div>
    <button type="button" class="btn-close" [attr.aria-label]="'CLOSE' | translate" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <div class="si-goals__toolbar">
      <label class="si-goals__search">
        <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
        <input
          type="search"
          class="form-control"
          [value]="customerSearch"
          [placeholder]="'SALES_INSIGHTS_HEALTH_SEARCH' | translate"
          [attr.aria-label]="'SALES_INSIGHTS_HEALTH_SEARCH' | translate"
          (input)="customerSearch = $any($event.target).value"
        />
      </label>
      <div class="si-goals__select">
        <lib-gung-input-multi-select
          [optionsList]="customerFilterOptions"
          [singleSelection]="true"
          [showEmptyIds]="true"
          [showId]="false"
          [suppressSearch]="true"
          previewField="name"
          (selectedValues)="selectCustomerFilter($event)"
        ></lib-gung-input-multi-select>
      </div>
      @if (checkError) {
        <span class="si-goals__error"><i class="fa-solid fa-triangle-exclamation me-1"></i>{{ checkError | translate }}</span>
      }
    </div>
    @if (goalCustomersLoading) {
      <div class="si-goals__state"><span class="spinner-border spinner-border-sm text-primary" role="status"></span></div>
    } @else if (goalCustomersError) {
      <div class="si-goals__state text-danger">{{ goalCustomersError | translate }}</div>
    } @else if (!visibleCustomers.length) {
      <div class="si-goals__state text-body-secondary">{{ 'SALES_INSIGHTS_NO_DATA' | translate }}</div>
    } @else {
      <div class="table-responsive si-goals__table">
        <table class="table table-sm table-hover mb-0">
          <thead>
            <tr>
              <th scope="col" class="si-goals__check-col">{{ 'SALES_INSIGHTS_GOAL_DONE' | translate }}</th>
              <th scope="col">{{ 'SALES_INSIGHTS_HEALTH_COL_CUSTOMER' | translate }}</th>
              <th scope="col">{{ 'SALES_INSIGHTS_HEALTH_COL_SALESPERSON' | translate }}</th>
              <th scope="col">{{ 'SALES_INSIGHTS_GOAL_NOTE' | translate }}</th>
              @if (canBook) {
                <th scope="col"></th>
              }
            </tr>
          </thead>
          <tbody>
            @for (customer of visibleCustomers; track customer.customerId) {
              <tr [class.si-goals__row--done]="customer.done">
                <td class="si-goals__check-col">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    [checked]="customer.done"
                    [disabled]="savingCustomerIds.has(customer.customerId)"
                    [attr.aria-label]="customer.customerName"
                    (change)="toggleDone(customer)"
                  />
                  @if (customer.doneSource === 'activity') {
                    <span class="si-goals__source" [title]="'SALES_INSIGHTS_GOAL_DONE_BY_ACTIVITY' | translate">
                      <i class="fa-regular fa-calendar-check"></i>
                    </span>
                  } @else if (hasOverride(customer)) {
                    <button
                      type="button"
                      class="si-goals__source si-goals__source--manual"
                      [title]="'SALES_INSIGHTS_GOAL_RESET' | translate"
                      [attr.aria-label]="'SALES_INSIGHTS_GOAL_RESET' | translate"
                      [disabled]="savingCustomerIds.has(customer.customerId)"
                      (click)="resetOverride(customer)"
                    >
                      <i class="fa-solid fa-hand-pointer"></i>
                    </button>
                  }
                </td>
                <td>
                  <span class="si-goals__name">{{ customer.customerName || customer.customerId }}</span>
                  <span class="si-goals__sub">
                    {{ customer.customerId }}
                    @if (customer.city) {
                      · {{ customer.city }}
                    }
                  </span>
                </td>
                <td class="si-goals__nowrap">
                  {{ customer.salespersonName || customer.salesperson }}
                </td>
                <td class="si-goals__note">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    [value]="customer.note || ''"
                    [placeholder]="'SALES_INSIGHTS_GOAL_NOTE_PLACEHOLDER' | translate"
                    [disabled]="savingCustomerIds.has(customer.customerId)"
                    (blur)="saveNote(customer, $any($event.target).value)"
                    (keydown.enter)="$any($event.target).blur()"
                  />
                  @if (customer.done && customer.doneSource === 'activity') {
                    <span class="si-goals__sub">
                      {{ 'SALES_INSIGHTS_GOAL_DONE_BY_ACTIVITY_ON' | translate: { date: customer.activityDate || '' } }}
                    </span>
                  } @else if (customer.done && (customer.doneBy || customer.doneAt)) {
                    <span class="si-goals__sub">
                      {{ 'SALES_INSIGHTS_GOAL_DONE_BY' | translate: { user: customer.doneBy || '', date: (customer.doneAt | date: 'yyyy-MM-dd') || '' } }}
                    </span>
                  } @else if (!customer.done && hasOverride(customer)) {
                    <span class="si-goals__sub">{{ 'SALES_INSIGHTS_GOAL_MARKED_NOT_DONE' | translate }}</span>
                  }
                </td>
                @if (canBook && openGoal) {
                  <td class="text-end">
                    <lib-sales-insights-book-activity
                      [customerId]="customer.customerId"
                      [goalId]="openGoal.id"
                      [goalTypes]="openGoal.activityTypes"
                      (booked)="onBooked()"
                    ></lib-sales-insights-book-activity>
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</ng-template>

<ng-template #goalForm let-modal>
  @if (form) {
    <div class="modal-header">
      <h4 class="modal-title">{{ (form.id ? 'SALES_INSIGHTS_GOAL_EDIT' : 'SALES_INSIGHTS_GOAL_NEW') | translate }}</h4>
      <button type="button" class="btn-close" [attr.aria-label]="'CLOSE' | translate" (click)="modal.dismiss()"></button>
    </div>
    <div class="modal-body si-goals__form">
      <div class="form-group">
        <label for="si-goal-title" class="control-label">{{ 'SALES_INSIGHTS_GOAL_TITLE' | translate }}</label>
        <input id="si-goal-title" type="text" class="form-control" [(ngModel)]="form.title" name="title" required />
      </div>
      <lib-gung-date-picker
        label="SALES_INSIGHTS_GOAL_DEADLINE"
        [currentselectedDate]="deadlineDate() || ''"
        [clearable]="true"
        (selectedDate)="setDeadline($event)"
      ></lib-gung-date-picker>
      <lib-gung-date-picker
        label="SALES_INSIGHTS_GOAL_START_DATE"
        [currentselectedDate]="startDateValue() || ''"
        [clearable]="true"
        (selectedDate)="setStartDate($event)"
      ></lib-gung-date-picker>
      <div class="form-group si-goals__type">
        <span class="control-label">{{ 'SALES_INSIGHTS_GOAL_ACTIVITY_TYPE' | translate }}</span>
        @if (!hasOwnType) {
          <div class="btn-group btn-group-sm si-goals__type-mode" role="group">
            <button
              type="button"
              class="btn btn-outline-primary"
              [class.active]="form.typeMode === 'existing'"
              [attr.aria-pressed]="form.typeMode === 'existing'"
              (click)="setTypeMode('existing')"
            >
              {{ 'SALES_INSIGHTS_GOAL_TYPE_EXISTING' | translate }}
            </button>
            <button
              type="button"
              class="btn btn-outline-primary"
              [class.active]="form.typeMode === 'new'"
              [attr.aria-pressed]="form.typeMode === 'new'"
              (click)="setTypeMode('new')"
            >
              {{ 'SALES_INSIGHTS_GOAL_TYPE_NEW' | translate }}
            </button>
          </div>
        }
        @if (form.typeMode === 'new') {
          <input
            id="si-goal-type-name"
            type="text"
            class="form-control"
            maxlength="60"
            [attr.aria-label]="'SALES_INSIGHTS_GOAL_TYPE_NAME' | translate"
            [placeholder]="form.title || ('SALES_INSIGHTS_GOAL_TYPE_NAME' | translate)"
            [(ngModel)]="form.newTypeName"
            name="newTypeName"
          />
        } @else {
          <lib-gung-input-multi-select
            [optionsList]="typeOptions"
            [showId]="false"
            previewField="name"
            (selectedValues)="selectCriteria('activityTypes', $event)"
          ></lib-gung-input-multi-select>
        }
      </div>
      @if (actionOptions.length) {
        <lib-gung-input-multi-select
          label="SALES_INSIGHTS_GOAL_ACTIVITY_ACTIONS"
          [optionsList]="actionOptions"
          [showId]="false"
          previewField="name"
          (selectedValues)="selectCriteria('activityActions', $event)"
        ></lib-gung-input-multi-select>
      }
      <div class="si-goals__section">
        <div class="si-goals__section-head">
          <span class="si-goals__section-title">{{ 'SALES_INSIGHTS_GOAL_CUSTOMERS' | translate }}</span>
          <span class="si-goals__preview-line" aria-live="polite">
            @if (previewLoading) {
              <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
            }
            @if (preview) {
              {{
                'SALES_INSIGHTS_GOAL_PREVIEW'
                  | translate: { customers: formatAmount(preview.customerCount), salespersons: formatAmount(preview.salespersonCount) }
              }}
            }
          </span>
        </div>
        <div class="si-goals__conditions">
          <lib-gung-input-multi-select
            label="SALES_INSIGHTS_FILTER_SALESPERSON"
            [optionsList]="salespersonOptions"
            [showId]="false"
            [enableLazyLoad]="true"
            [lazyLoadLimit]="50"
            [previewEmpty]="'SALES_INSIGHTS_ALL' | translate"
            previewField="name"
            (selectedValues)="selectConditions('salesperson', $event)"
          ></lib-gung-input-multi-select>
          <lib-gung-input-multi-select
            label="SALES_INSIGHTS_FILTER_COUNTRY"
            [optionsList]="countryOptions"
            [showId]="false"
            [previewEmpty]="'SALES_INSIGHTS_ALL' | translate"
            previewField="name"
            (selectedValues)="selectConditions('country', $event)"
          ></lib-gung-input-multi-select>
          @for (dimension of dimensionFields; track dimension.name) {
            <lib-gung-input-multi-select
              [label]="dimension.label"
              [optionsList]="dimension.options"
              [showId]="false"
              [enableLazyLoad]="true"
              [lazyLoadLimit]="50"
              [previewEmpty]="'SALES_INSIGHTS_ALL' | translate"
              previewField="name"
              (selectedValues)="selectDimension(dimension.name, $event)"
            ></lib-gung-input-multi-select>
          }
          <lib-gung-input-multi-select
            label="SALES_INSIGHTS_GOAL_FLAGS"
            [optionsList]="flagOptions"
            [showId]="false"
            [suppressSearch]="true"
            [previewEmpty]="'SALES_INSIGHTS_ALL' | translate"
            previewField="name"
            (selectedValues)="selectConditions('flags', $event)"
          ></lib-gung-input-multi-select>
        </div>
        <div class="form-check">
          <input
            id="si-goal-visit-required"
            type="checkbox"
            class="form-check-input"
            [ngModel]="form.visitRequiredOnly"
            (ngModelChange)="setVisitRequiredOnly($event)"
            name="visitRequiredOnly"
          />
          <label for="si-goal-visit-required" class="form-check-label">
            {{ 'SALES_INSIGHTS_GOAL_VISIT_REQUIRED_ONLY' | translate }}
          </label>
        </div>
        <div class="form-group si-goals__picker">
          <label for="si-goal-customer-search" class="control-label">{{ 'SALES_INSIGHTS_GOAL_ADD_CUSTOMERS' | translate }}</label>
          <label class="si-goals__search si-goals__search--block">
            <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            <input
              id="si-goal-customer-search"
              type="search"
              class="form-control"
              autocomplete="off"
              [value]="customerQuery"
              [placeholder]="'SALES_INSIGHTS_GOAL_CUSTOMER_SEARCH' | translate"
              (input)="searchCustomers($any($event.target).value)"
              (keydown.escape)="searchCustomers('')"
            />
          </label>
          @if (customerMatches.length) {
            <div class="list-group si-goals__matches" role="listbox">
              @for (customer of customerMatches; track customer.customerId) {
                <button
                  type="button"
                  class="list-group-item list-group-item-action si-goals__match"
                  role="option"
                  [disabled]="isPicked(customer.customerId)"
                  (click)="pickCustomer(customer)"
                >
                  <span class="si-goals__name">{{ customer.customerName || customer.customerId }}</span>
                  <span class="si-goals__sub">
                    {{ customer.customerId }}
                    @if (customer.city) {
                      · {{ customer.city }}
                    }
                    @if (customer.salespersonName || customer.salesperson) {
                      · {{ customer.salespersonName || customer.salesperson }}
                    }
                  </span>
                </button>
              }
            </div>
          }
        </div>
        @if (form.includeCustomerIds.length) {
          <div class="si-goals__chip-group">
            <span class="si-goals__chip-label">{{ 'SALES_INSIGHTS_GOAL_PICKED' | translate }}</span>
            @for (customerId of form.includeCustomerIds; track customerId) {
              <span class="si-goals__chip">
                {{ customerLabel(customerId) }}
                <button
                  type="button"
                  class="si-goals__chip-remove"
                  [attr.aria-label]="'SALES_INSIGHTS_GOAL_REMOVE_CUSTOMER' | translate"
                  (click)="dropCustomer(customerId)"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </span>
            }
          </div>
        }
        @if (form.excludeCustomerIds.length) {
          <div class="si-goals__chip-group">
            <span class="si-goals__chip-label">{{ 'SALES_INSIGHTS_GOAL_EXCLUDED' | translate }}</span>
            @for (customerId of form.excludeCustomerIds; track customerId) {
              <span class="si-goals__chip si-goals__chip--excluded">
                {{ customerLabel(customerId) }}
                <button
                  type="button"
                  class="si-goals__chip-remove"
                  [attr.aria-label]="'SALES_INSIGHTS_GOAL_RESTORE_CUSTOMER' | translate"
                  (click)="restoreCustomer(customerId)"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </span>
            }
          </div>
        }
        @if (preview?.salespersons?.length) {
          <div class="si-goals__chip-group">
            <span class="si-goals__chip-label">{{ 'SALES_INSIGHTS_GOAL_SALESPERSONS' | translate }}</span>
            @for (salesperson of preview!.salespersons; track salesperson.code) {
              <span class="si-goals__chip si-goals__chip--muted">
                {{ salesperson.code ? salesperson.name || salesperson.code : ('SALES_INSIGHTS_GOAL_NO_SALESPERSON' | translate) }}
                <b>{{ formatAmount(salesperson.customers) }}</b>
              </span>
            }
          </div>
        }
        @if (previewError) {
          <div class="si-goals__error">{{ previewError | translate }}</div>
        } @else if (preview && !preview.customerCount) {
          <div class="si-goals__state text-body-secondary">{{ 'SALES_INSIGHTS_GOAL_NO_CUSTOMERS' | translate }}</div>
        } @else if (preview?.customers?.length) {
          <ul class="si-goals__preview">
            @for (customer of preview!.customers; track customer.customerId) {
              <li>
                <span class="si-goals__preview-name">
                  <span class="si-goals__name">{{ customer.customerName || customer.customerId }}</span>
                  <span class="si-goals__sub">
                    {{ customer.customerId }}
                    @if (customer.salespersonName || customer.salesperson) {
                      · {{ customer.salespersonName || customer.salesperson }}
                    }
                  </span>
                </span>
                <button
                  type="button"
                  class="si-goals__icon si-goals__icon--danger"
                  [title]="'SALES_INSIGHTS_GOAL_REMOVE_CUSTOMER' | translate"
                  [attr.aria-label]="'SALES_INSIGHTS_GOAL_REMOVE_CUSTOMER' | translate"
                  (click)="dropCustomer(customer.customerId)"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </li>
            }
            @if (preview!.customerCount > preview!.customers.length) {
              <li class="si-goals__preview-more">
                {{ 'SALES_INSIGHTS_GOAL_MORE_CUSTOMERS' | translate: { count: formatAmount(preview!.customerCount - preview!.customers.length) } }}
              </li>
            }
          </ul>
        }
      </div>
      @if (formError) {
        <div class="alert alert-danger mt-3 mb-0">{{ formError | translate }}</div>
      }
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">{{ 'CANCEL' | translate }}</button>
      <button type="button" class="btn btn-primary" [disabled]="saving" (click)="saveForm(modal)">
        {{ 'SAVE' | translate }}
      </button>
    </div>
  }
</ng-template>
